import React, { FC, useEffect, useState } from 'react';
import { Box, Button, Divider, LinearProgress, Typography, useTheme } from '@mui/material';
import WidgetService from '../../services/WidgetService';
import StarRating from '../StarRating/StarRating';
import { useTranslation } from 'react-i18next';
import { WidgetNoData } from './WidgetNoData';
import ReviewMonitoringService from '../../services/ReviewMonitoringService';
import PlatformService from '../../services/PlatformService';
import { IPlatform } from '../../shared/interfaces/IPlatform';

interface IReviewScoreSplit {
  score: number;
  count: number;
}

const hasReviews = (scores: IReviewScoreSplit[]) => scores.some((score) => score.count > 0);

const WidgetReviewSummary: FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<Record<'hr' | 'customer', IReviewScoreSplit[]>>({
    hr: [],
    customer: [],
  });
  const [currentTab, setCurrentTab] = useState<'hr' | 'customer'>('customer');
  const [total, setTotal] = useState<number>(0);
  const hasHrReviews = hasReviews(data.hr);
  const hasCustomerReviews = hasReviews(data.customer);
  const shownTab = !hasCustomerReviews && hasHrReviews ? 'hr' : currentTab;

  useEffect(() => {
    const controller = new AbortController();

    const getReviews = async () => {
      const data = await ReviewMonitoringService.getMonitoring(
        false,
        'scores',
        setLoading,
        controller.signal
      );

      const platforms = await PlatformService.getPlatforms(setLoading, controller.signal);

      let sums = { hr: [0, 0, 0, 0, 0, 0], customer: [0, 0, 0, 0, 0, 0] };

      const promises = [];
      for (let monitoring of data) {
        const platformName = PlatformService.getPlatformName(monitoring.platformId, platforms);

        promises.push(
          new Promise<['hr' | 'customer', { scores: { score: number; count: number }[] }]>(
            (resolve, reject) => {
              WidgetService.reviewScoreSplitCompany(
                platformName,
                monitoring.company,
                setLoading,
                controller.signal
              ).then((res) =>
                resolve([PlatformService.getPlatformType(monitoring.platformId, platforms), res])
              );
            }
          )
        );
      }

      const results = await Promise.all(promises);

      for (let result of results) {
        for (const score of result[1].scores) {
          sums[result[0]][score.score] += score.count;
        }
      }

      const res: Record<'hr' | 'customer', IReviewScoreSplit[]> = { hr: [], customer: [] };
      for (const score of [5, 4, 3, 2, 1]) {
        res.hr.push({ score: score, count: sums.hr[score] });
        res.customer.push({ score: score, count: sums.customer[score] });
      }

      setData(res);
    };

    getReviews();

    return () => {
      controller.abort();
    };
  }, []);

  return (
    <Box>
      {loading ? (
        <Box>
          <LinearProgress sx={{ mt: theme.spacing(1), mb: theme.spacing(2) }} />
        </Box>
      ) : (
        <Box>
          <Box sx={{ justifyContent: 'flex-end', display: 'flex', gap: 0.5 }}>
            <Button
              size="small"
              variant={shownTab === 'hr' ? 'contained' : 'outlined'}
              onClick={() => setCurrentTab('hr')}
              disabled={!hasHrReviews && hasCustomerReviews}
              sx={{ py: 0, px: 1, minWidth: '75px', fontSize: '0.75rem' }}
            >
              {t('hr_portal')}
            </Button>
            <Button
              size="small"
              variant={shownTab === 'customer' ? 'contained' : 'outlined'}
              onClick={() => setCurrentTab('customer')}
              disabled={!hasCustomerReviews && hasHrReviews}
              sx={{ py: 0, px: 1, minWidth: '75px', fontSize: '0.75rem' }}
            >
              {t('customer_portal')}
            </Button>
          </Box>
          <Divider sx={{ mt: theme.spacing(1), mb: theme.spacing(2) }} />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {data[shownTab].length > 0 ? (
              data[shownTab].map((value: IReviewScoreSplit, index) => {
                return (
                  <Box key={index}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '1rem',
                      }}
                    >
                      <Box
                        sx={{
                          flexBasis: '40%',
                          textAlign: 'right',
                        }}
                      >
                        <StarRating value={value.score} />
                      </Box>
                      <Box
                        sx={{
                          flexBasis: '50%',
                          textAlign: 'left',
                        }}
                      >
                        <Typography>
                          {value.count}&nbsp;
                          {value.count === 1 ? t('review') : t('reviews')}
                          {total !== 0 && <>&nbsp;({Math.round((value.count / total) * 100)}%)</>}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                );
              })
            ) : (
              <>
                <Divider sx={{ mt: theme.spacing(1), mb: theme.spacing(2) }} />
                <WidgetNoData />
              </>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default WidgetReviewSummary;
